import React, { useEffect, useState } from 'react';
import { api, UPLOADS_BASE_URL } from '../services/api';
import ImageLightbox from './ImageLightbox';
const appLogo = new URL('../assets/bbyb-logo.png', import.meta.url);

const isImage = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
};

const isVideo = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase();
    return ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v', 'ogv'].includes(ext);
};

const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (str) => new Date(str).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

// Fallback URL for local/ECS mode; overridden by presigned URLs in Lambda mode
const fileUrlFallback = (file) => `${UPLOADS_BASE_URL}/uploads/${file.path}`;

/* ── Confirm dialog ───────────────────────────────────────────────────────── */
const ConfirmDialog = ({ open, title, body, onConfirm, onCancel }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onCancel} />
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-sm p-6">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </div>
                    <h3 className="text-sm font-bold text-gray-800">{title}</h3>
                </div>
                <div className="text-sm text-gray-500 mb-5">{body}</div>
                <div className="flex items-center gap-3 justify-end">
                    <button onClick={onCancel}
                        className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition">
                        Cancel
                    </button>
                    <button onClick={onConfirm}
                        className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition">
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── Icons ────────────────────────────────────────────────────────────────── */
const TrashIcon = ({ small = false }) => (
    <svg className={small ? 'w-3.5 h-3.5' : 'w-4 h-4'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

/* ── Checkbox ─────────────────────────────────────────────────────────────── */
const Checkbox = ({ checked, onChange, onClick }) => (
    <span
        role="checkbox"
        aria-checked={checked}
        onClick={onClick}
        onChange={onChange}
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition cursor-pointer
            ${checked
                ? 'bg-[#895129] border-[#895129]'
                : 'bg-white border-gray-300 hover:border-[#895129]'}`}
    >
        {checked && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
        )}
    </span>
);

const TIER_STYLES = {
    local: { dot: 'bg-gray-400',    labelTech: 'Local',  labelSimple: 'Local', text: 'text-gray-600',    bg: 'bg-gray-100'   },
    b2:    { dot: 'bg-amber-400',   labelTech: 'B2',     labelSimple: 'Cloud', text: 'text-amber-700',   bg: 'bg-amber-50'   },
    minio: { dot: 'bg-blue-400',    labelTech: 'MinIO',  labelSimple: 'Cloud', text: 'text-blue-700',    bg: 'bg-blue-50'    },
    agent: { dot: 'bg-emerald-400', labelTech: 'Agent',  labelSimple: 'Cloud', text: 'text-emerald-700', bg: 'bg-emerald-50' },
};

const TierBadge = ({ location, compact = false, simplified = false }) => {
    if (!location) return null;
    const s = TIER_STYLES[location];
    if (!s) return null;
    const label = simplified ? s.labelSimple : s.labelTech;
    if (compact) return <span className={`w-2 h-2 rounded-full ${s.dot} flex-shrink-0`} title={label} />;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.bg} ${s.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {label}
        </span>
    );
};

/* ════════════════════════════════════════════════════════════════════════════
   FileList
   ════════════════════════════════════════════════════════════════════════════ */
const FileList = ({ token, refreshTrigger, addNotification, simplified = false }) => {
    const [files, setFiles]               = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);
    const [viewMode, setViewMode]         = useState('grid');
    const [search, setSearch]             = useState('');
    const [deleting, setDeleting]         = useState(null);
    const [batchDeleting, setBatchDeleting] = useState(false);
    const [confirmFile, setConfirmFile]   = useState(null);   // single-file delete
    const [confirmBatch, setConfirmBatch] = useState(false);  // batch delete
    const [lightboxFile, setLightboxFile] = useState(null);
    const [selectMode, setSelectMode]     = useState(false);
    const [selectedIds, setSelectedIds]   = useState(new Set());
    const [fileUrls, setFileUrls]           = useState({});     // id → presigned URL cache
    const [thumbnailUrls, setThumbnailUrls] = useState({});     // id → presigned thumbnail URL

    // Resolve a display URL for a file: presigned if available, fallback otherwise.
    // Backend may return relative URLs (e.g. /uploads/...) for local storage;
    // prefix with the backend base so they work in dev (frontend on different port).
    const fileUrl = (file) => {
        const url = fileUrls[file.id];
        if (url && url.startsWith('/')) return `${UPLOADS_BASE_URL}${url}`;
        return url || fileUrlFallback(file);
    };

    const fetchFiles = async () => {
        try {
            const data = await api.listFiles();
            const fileList = data.files || [];
            setFiles(fileList);
            setError(null);

            // Resolve presigned URLs for images and videos in one batch call
            const media = fileList.filter(f => isImage(f.filename) || isVideo(f.filename));
            if (media.length > 0) {
                const { urls, thumbnailUrls: thumbs } = await api.getFileUrls(media.map(f => f.id));
                setFileUrls(prev => ({ ...prev, ...urls }));
                setThumbnailUrls(prev => ({ ...prev, ...thumbs }));
            }
        } catch {
            setError('Failed to load files.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (token) fetchFiles(); }, [token, refreshTrigger]);

    // Poll for sync events from other devices and auto-refresh file list
    useEffect(() => {
        if (!token) return;
        let cursor = 0;
        let cancelled = false;

        const poll = async () => {
            while (!cancelled) {
                try {
                    const data = await api.pollSyncEvents(cursor, 50);
                    const events = data.events || [];
                    if (events.length > 0) {
                        cursor = data.next_cursor || cursor;
                        // If any file was uploaded or deleted, refresh the list
                        const hasFileChange = events.some(e =>
                            e.event_type === 'file_uploaded' || e.event_type === 'file_deleted'
                        );
                        if (hasFileChange) fetchFiles();
                    }
                } catch {
                    // On error (e.g. network), wait before retrying
                    await new Promise(r => setTimeout(r, 10000));
                }
            }
        };

        poll();
        return () => { cancelled = true; };
    }, [token]);

    /* ── Selection helpers ── */
    const toggleSelect = (e, id) => {
        e.stopPropagation();
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const selectAll = () => setSelectedIds(new Set(filtered.map(f => f.id)));

    const clearSelection = () => {
        setSelectedIds(new Set());
        setSelectMode(false);
    };

    /* ── Single delete ── */
    const requestDelete = (e, file) => {
        e.stopPropagation();
        setConfirmFile(file);
    };

    const confirmSingleDelete = async () => {
        const file = confirmFile;
        setConfirmFile(null);
        setDeleting(file.id);
        try {
            await api.deleteFile(file.id);
            setFiles(prev => prev.filter(f => f.id !== file.id));
            addNotification?.({ type: 'success', title: 'File deleted', message: file.filename });
        } catch {
            addNotification?.({ type: 'error', title: 'Delete failed', message: file.filename });
        } finally {
            setDeleting(null);
        }
    };

    /* ── Batch delete ── */
    const executeBatchDelete = async () => {
        setConfirmBatch(false);
        setBatchDeleting(true);
        const ids = Array.from(selectedIds);
        const count = ids.length;
        const results = await Promise.allSettled(ids.map(id => api.deleteFile(id)));
        const failed = results.filter(r => r.status === 'rejected').length;
        setFiles(prev => prev.filter(f => !selectedIds.has(f.id)));
        setSelectedIds(new Set());
        setSelectMode(false);
        setBatchDeleting(false);
        if (failed === 0) {
            addNotification?.({ type: 'success', title: `${count} file${count !== 1 ? 's' : ''} deleted` });
        } else {
            addNotification?.({ type: 'warning', title: 'Some deletes failed', message: `${count - failed} deleted, ${failed} failed` });
        }
    };

    /* ── Grid item click ── */
    const handleGridClick = (file) => {
        if (selectMode) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.has(file.id) ? next.delete(file.id) : next.add(file.id);
                return next;
            });
        } else if (isImage(file.filename) || isVideo(file.filename)) {
            setLightboxFile(file);
        }
    };

    const filtered    = files.filter(f => f.filename.toLowerCase().includes(search.toLowerCase()));
    const allSelected = filtered.length > 0 && filtered.every(f => selectedIds.has(f.id));
    const mediaFiles  = filtered.filter(f => isImage(f.filename) || isVideo(f.filename));
    const lightboxIdx = lightboxFile ? mediaFiles.findIndex(f => f.id === lightboxFile.id) : -1;
    const totalSize   = files.reduce((a, f) => a + (f.size_original || 0), 0);

    if (loading) return (
        <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#895129]" />
        </div>
    );
    if (error) return (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
    );

    return (
        <>
            {/* Single-file confirm */}
            <ConfirmDialog
                open={!!confirmFile}
                title="Delete file"
                body={<>Are you sure you want to delete <span className="font-semibold text-gray-700 break-all">"{confirmFile?.filename}"</span>?<p className="text-xs text-gray-400 mt-1">This cannot be undone.</p></>}
                onConfirm={confirmSingleDelete}
                onCancel={() => setConfirmFile(null)}
            />

            {/* Batch confirm */}
            <ConfirmDialog
                open={confirmBatch}
                title={`Delete ${selectedIds.size} file${selectedIds.size !== 1 ? 's' : ''}`}
                body={<>Permanently delete <span className="font-semibold text-gray-700">{selectedIds.size} selected file{selectedIds.size !== 1 ? 's' : ''}</span>?<p className="text-xs text-gray-400 mt-1">This cannot be undone.</p></>}
                onConfirm={executeBatchDelete}
                onCancel={() => setConfirmBatch(false)}
            />

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

                {/* ── Toolbar ── */}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-100">
                    <div className="flex-1 min-w-[140px] relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input type="text" placeholder="Search files…" value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#895129] focus:border-transparent"
                        />
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap hidden sm:block">
                        {filtered.length} file{filtered.length !== 1 ? 's' : ''} · {formatSize(totalSize)}
                    </span>
                    {/* Storage legend */}
                    <div className="hidden md:flex items-center gap-2 border-l border-gray-100 pl-3">
                        {Object.entries(TIER_STYLES)
                            .filter(([, s], i, arr) =>
                                simplified
                                    ? arr.findIndex(([, t]) => t.labelSimple === s.labelSimple) === i
                                    : true
                            )
                            .map(([key, s]) => {
                                const label = simplified ? s.labelSimple : s.labelTech;
                                return (
                                    <span key={key} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.bg} ${s.text}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                                        {label}
                                    </span>
                                );
                            })
                        }
                    </div>
                    {/* Select toggle */}
                    <button
                        onClick={() => { setSelectMode(m => !m); if (selectMode) clearSelection(); }}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition
                            ${selectMode
                                ? 'bg-[#895129] text-white border-[#895129]'
                                : 'text-gray-500 border-gray-200 hover:border-[#895129] hover:text-[#895129]'}`}
                    >
                        {selectMode ? 'Cancel' : 'Select'}
                    </button>
                    {/* View toggle */}
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                        <button onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded-md transition ${viewMode === 'grid' ? 'bg-white shadow text-[#895129]' : 'text-gray-400 hover:text-gray-600'}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                        </button>
                        <button onClick={() => setViewMode('list')}
                            className={`p-1.5 rounded-md transition ${viewMode === 'list' ? 'bg-white shadow text-[#895129]' : 'text-gray-400 hover:text-gray-600'}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        {search ? (
                            <svg className="w-12 h-12 mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                            </svg>
                        ) : (
                            <img src={appLogo.href} alt="" className="w-16 h-16 object-contain opacity-40 mb-3" />
                        )}
                        <p className="text-sm font-medium">{search ? 'No files match your search' : 'No files yet'}</p>
                        {!search && <p className="text-xs mt-1">Tap the + button to upload files</p>}
                    </div>
                ) : viewMode === 'grid' ? (
                    /* ── Grid ── */
                    <div className="p-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-1.5">
                        {filtered.map(file => {
                            const selected = selectedIds.has(file.id);
                            return (
                                <div key={file.id}
                                    onClick={() => handleGridClick(file)}
                                    className={`group relative aspect-square rounded-lg overflow-hidden border transition
                                        ${selected
                                            ? 'border-[#895129] ring-2 ring-[#895129]/40 ring-offset-1'
                                            : 'border-gray-100 bg-gray-50'}
                                        ${selectMode || isImage(file.filename) || isVideo(file.filename) ? 'cursor-pointer' : ''}`}
                                >
                                    {isImage(file.filename) ? (
                                        <img src={fileUrl(file)} alt={file.filename}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                    ) : isVideo(file.filename) ? (
                                        <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-900 relative">
                                            {thumbnailUrls[file.id] && (
                                                <img src={thumbnailUrls[file.id]} alt={file.filename}
                                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                            )}
                                            <div className="relative z-10 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center">
                                                <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                            {!thumbnailUrls[file.id] && (
                                                <span className="text-[9px] text-white/60 uppercase font-semibold">{file.filename.split('.').pop()}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 bg-gray-50">
                                            <svg className="w-8 h-8 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A1 1 0 0111.293 2.707l3 3a1 1 0 01.293.707V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-[9px] text-gray-400 uppercase font-semibold">{file.filename.split('.').pop()}</span>
                                        </div>
                                    )}

                                    {/* Hover overlay — name + tier */}
                                    {!selectMode && (
                                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-1.5 py-1.5 translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                                            <p className="text-white text-[10px] font-medium leading-tight truncate mb-0.5">{file.filename}</p>
                                            <TierBadge location={file.storage_location} simplified={simplified} />
                                        </div>
                                    )}

                                    {/* Selection overlay */}
                                    {selected && (
                                        <div className="absolute inset-0 bg-[#895129]/25 rounded-lg pointer-events-none" />
                                    )}

                                    {/* Checkbox — top-left */}
                                    <div
                                        onClick={e => {
                                            if (!selectMode) setSelectMode(true);
                                            toggleSelect(e, file.id);
                                        }}
                                        className={`absolute top-1.5 left-1.5 transition
                                            ${selectMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                                    >
                                        <Checkbox checked={selected} />
                                    </div>

                                    {/* Delete button — top-right, hidden in select mode */}
                                    {!selectMode && (
                                        <button
                                            onClick={e => requestDelete(e, file)}
                                            disabled={deleting === file.id}
                                            className="absolute top-1.5 right-1.5 w-7 h-7 bg-white/90 hover:bg-red-600 text-gray-500 hover:text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-sm border border-gray-200 hover:border-red-600"
                                            title="Delete"
                                        >
                                            {deleting === file.id
                                                ? <div className="w-3.5 h-3.5 border-b-2 border-current rounded-full animate-spin" />
                                                : <TrashIcon small />
                                            }
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* ── List ── */
                    <div className="divide-y divide-gray-50">
                        {filtered.map(file => {
                            const selected = selectedIds.has(file.id);
                            return (
                                <div key={file.id}
                                    onClick={() => {
                                        if (selectMode) {
                                            setSelectedIds(prev => {
                                                const next = new Set(prev);
                                                next.has(file.id) ? next.delete(file.id) : next.add(file.id);
                                                return next;
                                            });
                                        } else if (isImage(file.filename) || isVideo(file.filename)) {
                                            setLightboxFile(file);
                                        }
                                    }}
                                    className={`flex items-center gap-3 px-4 py-2.5 group transition
                                        ${selected ? 'bg-[#895129]/10' : 'hover:bg-gray-50'}
                                        ${selectMode || isImage(file.filename) || isVideo(file.filename) ? 'cursor-pointer' : ''}`}
                                >
                                    {/* Checkbox */}
                                    {selectMode && (
                                        <Checkbox
                                            checked={selected}
                                            onClick={e => toggleSelect(e, file.id)}
                                        />
                                    )}

                                    {/* Thumbnail */}
                                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200 relative">
                                        {isImage(file.filename) ? (
                                            <img src={fileUrl(file)} alt={file.filename} className="w-full h-full object-cover" />
                                        ) : isVideo(file.filename) ? (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-800 relative">
                                                {thumbnailUrls[file.id] && (
                                                    <img src={thumbnailUrls[file.id]} alt={file.filename}
                                                        className="absolute inset-0 w-full h-full object-cover" />
                                                )}
                                                <svg className="relative z-10 w-5 h-5 text-white/80 drop-shadow" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A1 1 0 0111.293 2.707l3 3a1 1 0 01.293.707V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">{file.filename}</p>
                                        <p className="text-xs text-gray-400">{formatDate(file.created_at)} · {formatSize(file.size_original)}</p>
                                    </div>

                                    <TierBadge location={file.storage_location} simplified={simplified} />

                                    {/* Delete (hidden in select mode) */}
                                    {!selectMode && (
                                        <button
                                            onClick={e => requestDelete(e, file)}
                                            disabled={deleting === file.id}
                                            className="p-1.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition rounded-lg hover:bg-red-50"
                                            title="Delete"
                                        >
                                            {deleting === file.id
                                                ? <div className="w-4 h-4 border-b-2 border-red-400 rounded-full animate-spin" />
                                                : <TrashIcon />
                                            }
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Lightbox */}
                {lightboxFile && (
                    <ImageLightbox
                        image={lightboxFile}
                        imageUrl={fileUrl(lightboxFile)}
                        onClose={() => setLightboxFile(null)}
                        onPrev={lightboxIdx > 0 ? () => setLightboxFile(mediaFiles[lightboxIdx - 1]) : null}
                        onNext={lightboxIdx < mediaFiles.length - 1 ? () => setLightboxFile(mediaFiles[lightboxIdx + 1]) : null}
                    />
                )}
            </div>

            {/* ── Floating selection action bar ── */}
            {selectMode && (
                <div className="fixed bottom-20 sm:bottom-8 left-1/2 -translate-x-1/2 z-[200] flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-white rounded-2xl shadow-2xl px-3 py-2.5 sm:px-5 sm:py-3 pointer-events-auto max-w-[calc(100vw-2rem)]"
                    style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>

                    {/* Select all */}
                    <button
                        onClick={() => allSelected ? setSelectedIds(new Set()) : selectAll()}
                        className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-xl text-xs font-bold transition"
                    >
                        <Checkbox checked={allSelected} />
                        {allSelected ? 'Deselect all' : 'Select all'}
                    </button>

                    {selectedIds.size > 0 && <>
                        <div className="w-px h-4 bg-white/30" />

                        <span className="text-sm font-semibold whitespace-nowrap">
                            {selectedIds.size} selected
                        </span>

                        <div className="w-px h-4 bg-white/30" />

                        <button
                            onClick={() => setConfirmBatch(true)}
                            disabled={batchDeleting}
                            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 px-4 py-1.5 rounded-xl text-sm font-bold transition"
                        >
                            {batchDeleting
                                ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                : <TrashIcon small />
                            }
                            Delete {selectedIds.size}
                        </button>
                    </>}

                    <div className="w-px h-4 bg-white/30" />

                    <button
                        onClick={clearSelection}
                        className="text-white/60 hover:text-white text-xs font-medium transition"
                    >
                        Cancel
                    </button>
                </div>
            )}
        </>
    );
};

export default FileList;
